import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Languages, Loader2, Undo2 } from "lucide-react";

export const TRANSLATE_LANGS = [
  { value: "auto", label: "Automatica (paese del contatto)" },
  { value: "it", label: "Italiano" },
  { value: "en", label: "Inglese" },
  { value: "fr", label: "Francese" },
  { value: "es", label: "Spagnolo" },
  { value: "de", label: "Tedesco" },
];
export const LANG_LABEL = { it: "Italiano", en: "Inglese", fr: "Francese", es: "Spagnolo", de: "Tedesco" };

/**
 * Pulsante "Traduci" riusabile: traduce testo (+ oggetto opzionale) nella lingua scelta
 * e permette di ripristinare l'originale. `lang` può essere controllato dall'esterno
 * (es. tendina già presente nel dialog) oppure gestito internamente con la propria Select.
 */
export function TranslateControls({
  text, subject, onApply, lang: langProp, onLangChange, nation, showSelect = true,
  testidPrefix = "translate", label = "Traduci nella lingua selezionata", size = "sm",
}) {
  const [innerLang, setInnerLang] = useState("auto");
  const [busy, setBusy] = useState(false);
  const [original, setOriginal] = useState(null);
  const lang = langProp ?? innerLang;
  const setLang = onLangChange ?? setInnerLang;
  const hasText = !!(text || "").trim() || !!(subject || "").trim();

  const translate = async () => {
    if (!hasText) { toast.error("Scrivi o incolla prima un testo da tradurre"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/translate-message", { text: text || "", subject: subject || "", target_lang: lang, nation: nation || "" });
      setOriginal({ text: text || "", subject: subject || "" });
      onApply({ text: data.text, subject: data.subject });
      toast.success(`Testo tradotto in ${LANG_LABEL[data.target_lang] || data.target_label}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Traduzione fallita");
    } finally { setBusy(false); }
  };

  const restore = () => { if (original) { onApply(original); setOriginal(null); toast.success("Testo originale ripristinato"); } };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testidPrefix}-controls`}>
      {showSelect && (
        <Select value={lang} onValueChange={setLang}>
          <SelectTrigger className="h-8 w-56 text-xs" data-testid={`${testidPrefix}-lang-select`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {TRANSLATE_LANGS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <Button type="button" variant="secondary" size={size} onClick={translate} disabled={busy || !hasText} data-testid={`${testidPrefix}-btn`}
        className="rounded-full border border-border bg-transparent text-xs font-semibold text-foreground hover:bg-brema-hover disabled:opacity-40">
        {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Languages className="mr-1.5 h-3.5 w-3.5 text-brand" />}
        {busy ? "Traduzione…" : label}
      </Button>
      {original && (
        <Button type="button" variant="ghost" size={size} onClick={restore} data-testid={`${testidPrefix}-restore-btn`}
          className="rounded-full text-xs text-muted-foreground hover:bg-brema-hover hover:text-foreground">
          <Undo2 className="mr-1.5 h-3.5 w-3.5" /> Ripristina originale
        </Button>
      )}
    </div>
  );
}
